import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

// Lightbox Modal Component for Gallery Images - Uses portal to render above everything
export const GalleryLightbox = ({ images, isOpen, currentIndex, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNavigate('prev');
      if (e.key === 'ArrowRight') onNavigate('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    
    // Prevent body scroll when lightbox is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, onNavigate]);

  if (!isOpen || !images || images.length === 0) return null;

  const lightboxContent = (
    <div 
      className="fixed inset-0 bg-black flex items-center justify-center"
      onClick={onClose}
      style={{ 
        margin: 0, 
        padding: 0, 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Close Button - YouTube style, top right */}
      <button 
        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
        onClick={onClose}
        data-testid="lightbox-close-btn"
        style={{ zIndex: 100000 }}
      >
        <X className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
      </button>

      {/* Navigation - Previous */}
      {images.length > 1 && (
        <button 
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
          onClick={(e) => { e.stopPropagation(); onNavigate('prev'); }}
          data-testid="lightbox-prev-btn"
          style={{ zIndex: 100000 }}
        >
          <ChevronLeft className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
        </button>
      )}

      {/* Image - Full screen */}
      <img 
        src={images[currentIndex]} 
        alt={`Gallery ${currentIndex + 1}`} 
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        style={{ maxWidth: '100vw', maxHeight: '100vh' }}
      />

      {/* Navigation - Next */}
      {images.length > 1 && (
        <button 
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
          onClick={(e) => { e.stopPropagation(); onNavigate('next'); }}
          data-testid="lightbox-next-btn"
          style={{ zIndex: 100000 }}
        >
          <ChevronRight className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
        </button>
      )}

      {/* Image Counter - Bottom center */}
      {images.length > 1 && (
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-full text-white text-base font-medium"
          style={{ zIndex: 100000 }}
        >
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );

  // Use portal to render at document body level, above everything
  return createPortal(lightboxContent, document.body);
};

// Gallery Grid Component with Lightbox
export const GalleryGrid = ({ images, onDeleteImage, isOwner, accentColor = '#f59e0b' }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleNavigate = (direction) => {
    if (direction === 'prev') {
      setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
    } else {
      setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {images.slice(0, 4).map((image, index) => (
          <div 
            key={index} 
            className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
            onClick={() => openLightbox(index)}
          >
            <img 
              src={image} 
              alt={`Gallery ${index + 1}`} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white/0 group-hover:bg-white/90 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100">
                <ZoomIn className="w-5 h-5 text-gray-700" />
              </div>
            </div>
            {/* Delete Button for Owner */}
            {isOwner && onDeleteImage && (
              <button 
                onClick={(e) => { e.stopPropagation(); onDeleteImage(index); }} 
                className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {/* Image count badge on last visible image */}
            {index === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-xl">+{images.length - 4}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <GalleryLightbox 
        images={images}
        isOpen={lightboxOpen}
        currentIndex={currentIndex}
        onClose={() => setLightboxOpen(false)}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default GalleryGrid;
